import React, { useEffect, useMemo, useState, useContext } from "react";
import { createPortal } from "react-dom";
import "./../../../assets/css/admin/gestionPaiement/AdminPaiementsPage.css";
import Breadcrumb from "../../../components/breadcrumb/Breadcrumb";
import usePaiement from "../../../hooks/usePaiement";
import { AuthContext } from "../../../contexts/AuthContext.js";
import LoaderAdminUsers from "../../../components/loader/admin/gestionUtilisateur/LoaderAdminUsers";
import { generateFactureTemplate } from "../../../utils/factures";
import jsPDF from "jspdf";

export default function AdminPaiementsPage({ setAlert }) {
  const { getPaiements, updatePaiement } = usePaiement();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [paiements, setPaiements] = useState([]);

  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [type, setType] = useState("all");
  const [method, setMethod] = useState("all");
  const [archive, setArchive] = useState("all");
  const [sort, setSort] = useState("newest");

  // ---- Statut paiement : menu déroulant modifiable ----
  const [statusMenuId, setStatusMenuId] = useState(null);
  const [statusMenuPos, setStatusMenuPos] = useState(null);
  const [updatingStatusId, setUpdatingStatusId] = useState(null);

  const PAIEMENT_STATUS_OPTIONS = [
    { value: 1, label: "Payé", icon: "bi-check-circle-fill", cls: "statusItemPaye" },
    { value: 2, label: "En attente", icon: "bi-hourglass-split", cls: "statusItemAttente" },
    { value: 3, label: "Échoué", icon: "bi-x-circle-fill", cls: "statusItemEchoue" },
    { value: 4, label: "Gratuit", icon: "bi-gift-fill", cls: "statusItemGratuit" },
  ];

  const statusMenuPaiement = paiements.find(
    (x) => String(x._id) === String(statusMenuId),
  );

  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;

  const breadcrumbItems = [
    { label: "Mon Compte", link: "/utilisateur/compte" },
    { label: "Paiements" },
  ];

  const normalized = (v) =>
    String(v ?? "")
      .toLowerCase()
      .trim();

  const formatDate = (d) => (d ? new Date(d).toLocaleDateString("fr-FR") : "—");

  const toNumber = (v) => {
    const n = Number(String(v ?? "0").replace(/[^\d.-]/g, ""));
    return Number.isFinite(n) ? n : 0;
  };

  const hasDiscountInfo = (p) =>
    toNumber(p?.amountRemise) > 0 || !!String(p?.coupon || "").trim();

  const statusLabel = (s) => {
    switch (Number(s)) {
      case 1:
        return "Payé";
      case 2:
        return "En attente";
      case 3:
        return "Échoué";
      case 4:
        return "Gratuit";
      default:
        return "—";
    }
  };

  const statusBadgeClass = (s) => {
    switch (Number(s)) {
      case 1:
        return "badgeActive";
      case 2:
        return "badgeSoft";
      case 3:
        return "badgeBlocked";
      case 4:
        return "badgeGratuit";
      default:
        return "badgeSoft";
    }
  };

  // Corrigé : selon le modèle, 1=Abonnement, 2=Réabonnement, 3=Boost
  const typeLabel = (t) => {
    switch (Number(t)) {
      case 1:
        return "Abonnement";
      case 2:
        return "Réabonnement";
      case 3:
        return "Boost";
      default:
        return "Type inconnu";
    }
  };

  // Corrigé : gère explicitement Carte (4) et Gratuit (5)
  const methodLabel = (m) => {
    switch (Number(m)) {
      case 1:
        return "MVola";
      case 2:
        return "Orange Money";
      case 3:
        return "Airtel Money";
      case 4:
        return "Carte";
      case 5:
        return "Gratuit";
      default:
        return "Méthode inconnue";
    }
  };

  const [dateMin, setDateMin] = useState("");
  const [dateMax, setDateMax] = useState("");

  const archiveLabel = (a) => (Number(a) === 1 ? "Archivé" : "Non archivé");
  const archiveBadgeClass = (a) =>
    Number(a) === 1 ? "badgeArchived" : "badgeNotArchived";

  const csvEscape = (value) => {
    const s = String(value ?? "");
    const escaped = s.replace(/"/g, '""');
    return `"${escaped}"`;
  };

  const startOfDayTs = (ymd) => {
    if (!ymd) return null;
    const [y, m, d] = ymd.split("-").map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d, 0, 0, 0, 0).getTime();
  };

  const endOfDayTs = (ymd) => {
    if (!ymd) return null;
    const [y, m, d] = ymd.split("-").map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d, 23, 59, 59, 999).getTime();
  };

  const paiementDateTs = (p) => {
    const v = p?.createdAt || p?.date;
    const t = v ? new Date(v).getTime() : NaN;
    return Number.isFinite(t) ? t : null;
  };

  const buildPaiementCsvHeader = () => {
    return [
      "paiement_id",
      "date_creation",
      "date",
      "status",
      "type",
      "methode",
      "montant_initial",
      "remise",
      "coupon",
      "montant_paye",
      "devise",
      "archive",
      "user_nom",
      "user_prenom",
      "user_email",
      "article_id",
      "article_titre",
      "article_prix",
      "source_phone_number",
      "idStripe",
      "idMvola",
      "idAirtel",
      "airtelMoneyId",
      "idOrangeMoney",
      "idTransactionOrange",
    ]
      .map(csvEscape)
      .join(";");
  };

  const buildPaiementCsvRow = (p) => {
    const userNom = p?.idUser?.nom ?? "";
    const userPrenom = p?.idUser?.prenom ?? "";
    const userEmail = p?.idUser?.email ?? "";

    const articleId = p?.idArticle?._id ?? p?.idArticle ?? "";
    const articleTitre = p?.idArticle?.titre ?? "";
    const articlePrix = p?.idArticle?.prix ?? "";

    const row = [
      p?._id ?? "",
      p?.createdAt ? new Date(p.createdAt).toISOString() : "",
      p?.date ? new Date(p.date).toISOString() : "",
      statusLabel(p?.status),
      typeLabel(p?.type),
      methodLabel(p?.methodPayment),
      p?.amountInitial ?? "",
      p?.amountRemise ?? "",
      p?.coupon ?? "",
      p?.amount ?? "",
      p?.devise ?? "",
      archiveLabel(p?.archive),
      userNom,
      userPrenom,
      userEmail,
      articleId,
      articleTitre,
      articlePrix,
      p?.sourcePhoneNumber ?? "",
      p?.idStripe ?? "",
      p?.idMvola ?? "",
      p?.idAirtel ?? "",
      p?.airtelMoneyId ?? "",
      p?.idOrangeMoney ?? "",
      p?.idTransactionOrange ?? "",
    ];

    return row.map(csvEscape).join(";");
  };

  const downloadCsv = (filename, csvContent) => {
    const BOM = "\uFEFF";
    const blob = new Blob([BOM + csvContent], {
      type: "text/csv;charset=utf-8",
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const exportPaiementCsv = (p) => {
    if (!p?._id) return;

    const header = buildPaiementCsvHeader();
    const row = buildPaiementCsvRow(p);
    const csv = `${header}\n${row}`;

    const safeId = String(p?.idUser?.nom);

    const filename = `paiement_${safeId}.csv`;

    downloadCsv(filename, csv);

    setAlert?.({ type: "success", message: "CSV généré." });
  };

  const exportFilteredCsv = (list) => {
    if (!Array.isArray(list) || list.length === 0) {
      setAlert?.({ type: "danger", message: "Aucun paiement à exporter." });
      return;
    }
    const header = buildPaiementCsvHeader();
    const rows = list.map(buildPaiementCsvRow);
    const csv = [header, ...rows].join("\n");
    downloadCsv(`paiements_${new Date().toISOString().slice(0, 10)}.csv`, csv);
    setAlert?.({ type: "success", message: "CSV exporté." });
  };

  const fetchPaiements = async () => {
    setLoading(true);
    try {
      const res = await getPaiements();
      const data = res?.data?.data || res?.data || [];
      setPaiements(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setPaiements([]);
      setAlert?.({
        type: "danger",
        message: "Impossible de récupérer les paiements",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPaiements();
  }, []);

  const onChangeDateMin = (nextMin) => {
    setDateMin(nextMin);
    if (nextMin && dateMax && startOfDayTs(dateMax) < startOfDayTs(nextMin)) {
      setDateMax(nextMin);
    }
  };

  const onChangeDateMax = (nextMax) => {
    setDateMax(nextMax);
    if (nextMax && dateMin && startOfDayTs(nextMax) < startOfDayTs(dateMin)) {
      setDateMin(nextMax);
    }
  };

  const clearDates = () => {
    setDateMin("");
    setDateMax("");
  };

  const filteredPaiements = useMemo(() => {
    let list = [...(paiements || [])];

    if (q.trim()) {
      const query = normalized(q);
      list = list.filter((p) => {
        const fullName =
          `${p?.idUser?.nom ?? ""} ${p?.idUser?.prenom ?? ""}`.trim();
        return (
          normalized(fullName).includes(query) ||
          normalized(p?.idUser?.email).includes(query) ||
          normalized(p?.idUser?.tel).includes(query)
        );
      });
    }

    if (status !== "all")
      list = list.filter((p) => String(p?.status) === String(status));
    if (type !== "all")
      list = list.filter((p) => String(p?.type) === String(type));
    if (method !== "all")
      list = list.filter((p) => String(p?.methodPayment) === String(method));
    if (archive !== "all")
      list = list.filter((p) => String(p?.archive) === String(archive));

    if (sort === "newest") {
      list.sort(
        (a, b) => new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0),
      );
    } else if (sort === "oldest") {
      list.sort(
        (a, b) => new Date(a?.createdAt || 0) - new Date(b?.createdAt || 0),
      );
    } else if (sort === "amountAsc") {
      list.sort((a, b) => Number(a?.amount || 0) - Number(b?.amount || 0));
    } else if (sort === "amountDesc") {
      list.sort((a, b) => Number(b?.amount || 0) - Number(a?.amount || 0));
    } else if (sort === "name") {
      list.sort((a, b) => {
        const an = normalized(
          `${a?.idUser?.nom ?? ""} ${a?.idUser?.prenom ?? ""}`,
        );
        const bn = normalized(
          `${b?.idUser?.nom ?? ""} ${b?.idUser?.prenom ?? ""}`,
        );
        return an.localeCompare(bn);
      });
    }

    const minTs = dateMin ? startOfDayTs(dateMin) : null;
    const maxTs = dateMax ? endOfDayTs(dateMax) : null;

    if (minTs !== null || maxTs !== null) {
      list = list.filter((p) => {
        const t = paiementDateTs(p);
        if (t === null) return false;
        if (minTs !== null && t < minTs) return false;
        if (maxTs !== null && t > maxTs) return false;
        return true;
      });
    }

    return list;
  }, [paiements, q, status, type, method, archive, sort, dateMin, dateMax]);

  useEffect(() => {
    setPage(1);
  }, [q, status, type, method, archive, sort, dateMin, dateMax]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredPaiements.length / PAGE_SIZE),
  );
  const safePage = Math.min(page, totalPages);

  const pagedPaiements = filteredPaiements.slice(
    (safePage - 1) * PAGE_SIZE,
    safePage * PAGE_SIZE,
  );

  useEffect(() => {
    if (!statusMenuId) return;
    const close = () => {
      setStatusMenuId(null);
      setStatusMenuPos(null);
    };
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    document.addEventListener("click", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
      document.removeEventListener("click", close);
    };
  }, [statusMenuId]);

  const handleChangeStatus = async (p, newStatus) => {
    if (!p?._id || Number(p.status) === Number(newStatus)) {
      setStatusMenuId(null);
      setStatusMenuPos(null);
      return;
    }

    try {
      setUpdatingStatusId(p._id);
      await updatePaiement(p._id, { status: newStatus });

      setPaiements((prev) =>
        (prev || []).map((x) =>
          String(x._id) === String(p._id) ? { ...x, status: newStatus } : x,
        ),
      );

      setAlert?.({
        type: "success",
        message: `Statut mis à jour : ${statusLabel(newStatus)}.`,
      });
    } catch (e) {
      console.error(e);
      setAlert?.({
        type: "danger",
        message: "Impossible de modifier le statut.",
      });
    } finally {
      setUpdatingStatusId(null);
      setStatusMenuId(null);
      setStatusMenuPos(null);
    }
  };

  const toggleArchive = async (p) => {
    try {
      const next = Number(p?.archive) === 1 ? 2 : 1;
      await updatePaiement(p._id, { archive: next });

      setPaiements((prev) =>
        (prev || []).map((x) =>
          String(x._id) === String(p._id) ? { ...x, archive: next } : x,
        ),
      );
    } catch (e) {
      console.error(e);
      setAlert?.({
        type: "danger",
        message: "Action impossible pour le moment.",
      });
    }
  };

  // Fonction pour exporter la facture en PDF
  const exportFacturePdf = (facture) => {
    const typeFacture = typeLabel(facture.type);
    const methodePaiement = methodLabel(facture.methodPayment);

    const remise = toNumber(facture.amountRemise);
    const initialRaw = toNumber(facture.amountInitial);
    const coupon = String(facture.coupon || "").trim();
    const showDiscount = hasDiscountInfo(facture);
    const initial =
      initialRaw > 0 ? initialRaw : toNumber(facture.amount) + remise;

    const factureData = {
      logo: "https://deal.mg/static/media/logo.af813824bcc0d21719a7.png",
      factureTitre: `Facture ${facture._id}`,
      factureUtilisateur: `${facture.idUser.nom} ${facture.idUser.prenom}`,
      factureReference: facture._id,
      factureDate: new Date(
        facture.date || facture.createdAt,
      ).toLocaleDateString("fr-FR"),
      factureProduits: facture.idArticle.titre,
      factureTotal: String(facture.amount),
      factureType: typeFacture,
      factureMethode: methodePaiement,
      factureTotal2: `${facture.amount} ${facture.devise}`,
    };

    const doc = new jsPDF();

    // En-tête : logo et titre
    const logo = new Image();
    logo.src = factureData.logo;
    doc.addImage(logo, "PNG", 10, 10, 40, 10);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(22);
    doc.setTextColor(40, 40, 40);
    doc.text("FACTURE", 105, 35, { align: "center" });

    doc.setLineWidth(1);
    doc.setDrawColor(40, 40, 40);
    doc.setFontSize(14);
    doc.line(10, 40, 200, 40);
    doc.text(`Référence : ${factureData.factureReference}`, 10, 50);
    doc.text(`Date : ${factureData.factureDate}`, 10, 60);

    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.text(`Client : ${factureData.factureUtilisateur}`, 10, 70);
    doc.text(`Type de facture : ${factureData.factureType}`, 10, 80);
    doc.text(`Méthode de paiement : ${factureData.factureMethode}`, 10, 90);

    // Tableau produit
    doc.setFont("helvetica", "bold");
    doc.setFillColor(52, 152, 219);
    doc.rect(10, 100, 180, 10, "F");
    doc.setTextColor(255, 255, 255);
    doc.text("Produit", 15, 106);
    doc.text("Montant", 160, 106);

    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "normal");
    doc.text(factureData.factureProduits, 15, 120);
    doc.text(
      showDiscount ? `${initial} ${facture.devise}` : factureData.factureTotal,
      160,
      120,
    );

    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.5);
    doc.line(155, 100, 155, 130);
    doc.rect(10, 100, 180, 30);

    // Bloc remise/coupon (uniquement si présent)
    let y = 140;
    if (showDiscount) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);
      doc.setTextColor(60, 60, 60);
      if (remise > 0) {
        doc.text(
          `Remise${coupon ? ` (code ${coupon})` : ""} : - ${remise} ${facture.devise}`,
          140,
          y,
          { align: "right" },
        );
        y += 10;
      } else if (coupon) {
        doc.text(`Code promo appliqué : ${coupon}`, 140, y, { align: "right" });
        y += 10;
      }
    }

    // Total
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text("Total :", 100, y);
    doc.setFontSize(16);
    doc.setTextColor(52, 152, 219);
    doc.text(factureData.factureTotal2, 160, y, { align: "right" });

    // Footer
    doc.setFontSize(10);
    doc.setTextColor(150, 150, 150);
    doc.text("© Deal — Facture électronique", 105, 290, { align: "center" });

    doc.save("facture.pdf");
  };

  return (
    <div className="adminPaiementPage">
      <div className="container">
        <Breadcrumb items={breadcrumbItems} />

        <div className="adminPaiementGrid">
          <section className="adminPaiementCard adminPaiementCardCenter">
            <div className="adminPaiementHeader">
              <div>
                <div className="adminPaiementTitle">Gestion des paiements</div>
                <div className="adminPaiementSubtitle">
                  {loading
                    ? "Chargement..."
                    : `${filteredPaiements.length} paiement(s)`}
                </div>
              </div>

              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <button
                  className="adminPaiementBtn adminPaiementBtnSoft"
                  onClick={() => exportFilteredCsv(filteredPaiements)}
                  disabled={loading || filteredPaiements.length === 0}
                  title="Exporter la liste filtrée en CSV"
                >
                  <i className="bi bi-download" /> Export CSV
                </button>

                <button
                  className="adminPaiementBtn adminPaiementBtnSoft"
                  onClick={fetchPaiements}
                  disabled={loading}
                >
                  <i className="bi bi-arrow-clockwise" /> Rafraîchir
                </button>
              </div>
            </div>

            {/* FILTERS */}
            <div className="adminPaiementFilters">
              <div className="adminPaiementSearch">
                <i className="bi bi-search" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Rechercher : nom, email, téléphone..."
                />
              </div>

              <div className="adminPaiementSelects">
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option value="all">Statut</option>
                  <option value="1">Payé</option>
                  <option value="2">En attente</option>
                  <option value="4">Gratuit</option>
                  <option value="3">Échoué</option>
                </select>

                <select value={type} onChange={(e) => setType(e.target.value)}>
                  <option value="all">Type</option>
                  <option value="1">Abonnement</option>
                  <option value="2">Réabonnement</option>
                  <option value="3">Boost</option>
                </select>

                <select
                  value={method}
                  onChange={(e) => setMethod(e.target.value)}
                >
                  <option value="all">Méthode</option>
                  <option value="1">MVola</option>
                  <option value="2">Orange Money</option>
                  <option value="3">Airtel Money</option>
                  <option value="4">Carte</option>
                  <option value="5">Gratuit</option>
                </select>

                <select
                  value={archive}
                  onChange={(e) => setArchive(e.target.value)}
                >
                  <option value="all">Archive</option>
                  <option value="2">Non archivé</option>
                  <option value="1">Archivé</option>
                </select>

                <select value={sort} onChange={(e) => setSort(e.target.value)}>
                  <option value="newest">Plus récents</option>
                  <option value="oldest">Plus anciens</option>
                  <option value="amountDesc">Montant ↓</option>
                  <option value="amountAsc">Montant ↑</option>
                  <option value="name">Nom A → Z</option>
                </select>
              </div>
              <div className="adminPaiementDateRange">
                <div className="adminPaiementDateField">
                  <label className="adminPaiementDateLabel">Date min</label>
                  <input
                    type="date"
                    value={dateMin}
                    onChange={(e) => onChangeDateMin(e.target.value)}
                    max={dateMax || undefined}
                  />
                </div>

                <div className="adminPaiementDateField">
                  <label className="adminPaiementDateLabel">Date max</label>
                  <input
                    type="date"
                    value={dateMax}
                    onChange={(e) => onChangeDateMax(e.target.value)}
                    min={dateMin || undefined}
                  />
                </div>

                {(dateMin || dateMax) && (
                  <button
                    type="button"
                    className="adminPaiementBtn adminPaiementBtnSoft"
                    onClick={clearDates}
                    title="Réinitialiser la période"
                  >
                    <i className="bi bi-x-circle" /> Période
                  </button>
                )}
              </div>
            </div>

            {loading ? (
              <LoaderAdminUsers />
            ) : pagedPaiements.length === 0 ? (
              <div className="adminPaiementEmpty">
                <i className="bi bi-credit-card" /> Aucun paiement
              </div>
            ) : (
              <div className="adminPaiementTableWrap">
                <table className="adminPaiementTable">
                  <thead>
                    <tr>
                      <th>Utilisateur</th>
                      <th>Article</th>
                      <th className="hideOnMobile">Type</th>
                      <th className="hideOnMobile">Méthode</th>
                      <th className="hideOnMobile">Phone</th>
                      <th>Statut</th>
                      <th>Coupon</th>
                      <th>Prix</th>
                      <th>Remise / Coupon</th>
                      <th>Payer</th>
                      {/* <th className="hideOnMobile">Archive</th> */}
                      {user.role === 1 && (<th className="hideOnMobile">Date</th>)}

                      {user.role === 1 && (<th className="colActions">Action</th>)}

                    </tr>
                  </thead>

                  <tbody>
                    {pagedPaiements.map((p) => {
                      const fullName =
                        `${p?.idUser?.nom ?? ""} ${p?.idUser?.prenom ?? ""}`.trim();
                      const discount = hasDiscountInfo(p);
                      const initialPriceLabel =
                        toNumber(p?.amountInitial) > 0
                          ? `${p.amountInitial} ${p?.devise || ""}`.trim() : "—"

                      const paiementDate = p?.date || p?.createdAt;
                      const remiseLabel =
                        toNumber(p?.amountRemise) > 0
                          ? `${p.amountRemise} ${p?.devise || ""}`.trim()
                          : p?.coupon
                            ? `Code ${p.coupon}`
                            : "";
                      const finalPriceLabel = `${p?.amount ?? "—"} ${p?.devise || ""}`.trim();

                      return (
                        <tr key={p._id}>
                          <td>
                            <div className="adminPaiementCellUser">
                              <div className="adminPaiementCellUserText">
                                <div className="adminPaiementName">
                                  {fullName || "—"}
                                </div>

                                <div className="adminPaiementMobileMeta">
                                  <div>
                                    <i className="bi bi-tags" />{" "}
                                    {typeLabel(p.type)}
                                  </div>
                                  <div>
                                    <i className="bi bi-wallet2" />{" "}
                                    {methodLabel(p.methodPayment)}
                                  </div>
                                  <div>
                                    <i className="bi bi-archive" />{" "}
                                    {archiveLabel(p.archive)}
                                  </div>
                                  <div>
                                    <i className="bi bi-calendar3" />{" "}
                                    {formatDate(paiementDate)}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>

                          <td className="hideOnMobile">{p?.idArticle?.titre}</td>
                          <td className="hideOnMobile">{typeLabel(p.type)}</td>
                          <td className="hideOnMobile">
                            {methodLabel(p.methodPayment)}
                          </td>

                          <td className="hideOnMobile">
                            {p?.sourcePhoneNumber || "—"}
                          </td>

                          <td style={{ position: "relative" }}>
                            {user.role === 1 ? (
                              <button
                                type="button"
                                className={`adminPaiementBadge adminPaiementBadgeClickable ${statusBadgeClass(p.status)}`}
                                onClick={(e) => {
                                  e.stopPropagation();

                                  if (statusMenuId === p._id) {
                                    setStatusMenuId(null);
                                    setStatusMenuPos(null);
                                    return;
                                  }

                                  const rect = e.currentTarget.getBoundingClientRect();
                                  const menuHeight = 190;
                                  const openUp = window.innerHeight - rect.bottom < menuHeight;

                                  setStatusMenuPos({
                                    top: openUp ? rect.top - 8 : rect.bottom + 8,
                                    left: rect.left,
                                    openUp,
                                  });
                                  setStatusMenuId(p._id);
                                }}
                                disabled={updatingStatusId === p._id}
                              >
                                {updatingStatusId === p._id ? (
                                  <span className="adminPaiementStatusSpinner" />
                                ) : (
                                  <>
                                    {statusLabel(p.status)}
                                    <i className="bi bi-chevron-down adminPaiementBadgeCaret" />
                                  </>
                                )}
                              </button>
                            ) : (
                              <span className={`adminPaiementBadge ${statusBadgeClass(p.status)}`}>
                                {statusLabel(p.status)}
                              </span>
                            )}

                            {statusMenuId &&
                              statusMenuPos &&
                              statusMenuPaiement &&
                              createPortal(
                                <div
                                  className="adminPaiementStatusDropdown"
                                  style={{
                                    top: statusMenuPos.top,
                                    left: statusMenuPos.left,
                                    transform: `translateY(${statusMenuPos.openUp ? "-100%" : "0"})`,
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {PAIEMENT_STATUS_OPTIONS.map((opt) => (
                                    <div
                                      key={opt.value}
                                      className={`adminPaiementStatusItem ${opt.cls} ${Number(statusMenuPaiement.status) === opt.value ? "isCurrent" : ""
                                        }`}
                                      onClick={() => handleChangeStatus(statusMenuPaiement, opt.value)}
                                    >
                                      <i className={`bi ${opt.icon}`} />
                                      {opt.label}
                                      {Number(statusMenuPaiement.status) === opt.value && (
                                        <i className="bi bi-check-lg adminPaiementStatusCheck" />
                                      )}
                                    </div>
                                  ))}
                                </div>,
                                document.body,
                              )}
                          </td>
                          <td className="hideOnMobile">
                            <b>{p?.coupon || "—"}</b>
                          </td>

                          <td>
                            <b>{initialPriceLabel ?? "—"}</b>
                          </td>

                          <td>
                            <b>{remiseLabel ?? "—"}</b>
                          </td>
                          <td>
                            <b>{finalPriceLabel ?? "—"}</b>
                          </td>
                          {user.role === 1 && (<td className="hideOnMobile">
                            {formatDate(paiementDate)}
                          </td>)}

                          {user.role === 1 && (<td className="colActions">
                            <div className="adminPaiementActions">
                              <button
                                type="button"
                                className="adminPaiementIconBtn"
                                onClick={() => exportPaiementCsv(p)}
                                title="Exporter ce paiement en CSV"
                                disabled={p.status == 2}
                              >
                                <i className="bi bi-filetype-csv" />
                              </button>

                              {/* <button
                                className="adminPaiementIconBtn"
                                key={p._id}
                                onClick={() => exportFacturePdf(p)}
                                disabled={p.status == 2}
                                title="Exporter la facture en PDF"
                              >
                                <i className="bi bi-file-earmark-pdf" />
                              </button> */}

                              <button
                                type="button"
                                className="adminPaiementIconBtn"
                                onClick={() => toggleArchive(p)}
                                title={
                                  Number(p.archive) === 1
                                    ? "Désarchiver"
                                    : "Archiver"
                                }
                              >
                                <i
                                  className={`bi ${Number(p.archive) === 1
                                    ? "bi-arrow-counterclockwise"
                                    : "bi-archive"
                                    }`}
                                />
                              </button>
                            </div>
                          </td>)}


                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* PAGINATION */}
            {!loading && filteredPaiements.length > 0 && (
              <div className="adminPaiementPagination">
                <button
                  type="button"
                  className="adminPaiementIconBtn"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={safePage <= 1}
                  title="Page précédente"
                >
                  <i className="bi bi-chevron-left" />
                </button>

                <div className="adminPaiementSubtitle" style={{ marginTop: 0 }}>
                  Page <b>{safePage}</b> / {totalPages}
                </div>

                <button
                  type="button"
                  className="adminPaiementIconBtn"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage >= totalPages}
                  title="Page suivante"
                >
                  <i className="bi bi-chevron-right" />
                </button>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
